/** Delivery rules & location assignment, price lists and rebate tracking for a supplier's
 * governance profile. Extracted out of GovernanceTab so the same section can be reused as a
 * step in the Supplier Onboarding wizard (Demo Mode only, local-only - no backend
 * persistence yet). See `src/mocks/data/supplierGovernanceData.ts`. */

import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../../../components/ui/Section';
import { SupplierGovernanceProfile } from '../../../../../mocks/data/supplierGovernanceData';

export interface DeliveryAndPricingSectionProps {
  profile: SupplierGovernanceProfile;
}

const DeliveryAndPricingSection: React.FC<DeliveryAndPricingSectionProps> = ({ profile }) => {
  const { t } = useTranslation('suppliers');

  return (
    <>
      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textSM"
            fontWeight={600}
            sx={{ mb: 2 }}>
            {t('deliveryRulesLocations', 'Delivery rules & location assignment')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('leadTimeDays', 'Lead time (days)')}</TableCell>
                <TableCell>{t('deliveryDays', 'Delivery days')}</TableCell>
                <TableCell>{t('minimumOrderValue', 'Minimum order value')}</TableCell>
                <TableCell>{t('assignedStores', 'Assigned stores')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {profile.deliveryRules.length === 0 && (
                <TableRow><TableCell colSpan={4}>{t('noDeliveryRules', 'No delivery rules captured yet.')}</TableCell></TableRow>
              )}
              {profile.deliveryRules.map((rule) => (
                <TableRow key={rule.id}>
                  <TableCell>{rule.leadTimeDays}</TableCell>
                  <TableCell>{rule.deliveryDays.join(', ')}</TableCell>
                  <TableCell>{rule.minimumOrderValue}</TableCell>
                  <TableCell>{rule.assignedStoreNumbers.join(', ')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textSM"
            fontWeight={600}
            sx={{ mb: 2 }}>
            {t('priceLists', 'Price lists')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('itemNumber', 'Item number')}</TableCell>
                <TableCell>{t('itemName', 'Item name')}</TableCell>
                <TableCell>{t('price', 'Price')}</TableCell>
                <TableCell>{t('effectiveFrom', 'Effective from')}</TableCell>
                <TableCell>{t('status', 'Status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {profile.priceList.length === 0 && (
                <TableRow><TableCell colSpan={5}>{t('noPriceList', 'No price list entries captured yet.')}</TableCell></TableRow>
              )}
              {profile.priceList.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>{entry.itemNumber}</TableCell>
                  <TableCell>{entry.itemName}</TableCell>
                  <TableCell>{entry.price.toFixed(2)}</TableCell>
                  <TableCell>{entry.effectiveFrom}</TableCell>
                  <TableCell>
                    {entry.pendingApproval
                      ? <Chip label={t('pendingApproval', 'Pending approval')}
                        color="warning"
                        size="small" />
                      : <Chip label={t('active', 'Active')}
                        color="success"
                        size="small" />}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textSM"
            fontWeight={600}
            sx={{ mb: 2 }}>
            {t('rebateTracking', 'Rebate tracking')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('period', 'Period')}</TableCell>
                <TableCell>{t('rebatePercentage', 'Rebate %')}</TableCell>
                <TableCell>{t('thresholdSpend', 'Threshold spend')}</TableCell>
                <TableCell>{t('accruedYtd', 'Accrued YTD')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {profile.rebateAgreements.length === 0 && (
                <TableRow><TableCell colSpan={4}>{t('noRebates', 'No rebate agreements captured yet.')}</TableCell></TableRow>
              )}
              {profile.rebateAgreements.map((rebate) => (
                <TableRow key={rebate.id}>
                  <TableCell>{rebate.periodLabel}</TableCell>
                  <TableCell>{rebate.rebatePercentage}%</TableCell>
                  <TableCell>{rebate.thresholdSpend.toLocaleString()}</TableCell>
                  <TableCell>{rebate.accruedYtd.toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Section>
    </>
  );
};

export default DeliveryAndPricingSection;
